"use client";

/**
 * Renderer per il blocco `homeBlockRef` del page builder: carica un HomeBlock
 * dal backend (V15) e lo rende con la gallery corrispondente.
 *
 * Nota: qui viene usata la lista pubblica (solo blocchi enabled=true). Se il
 * blocco è stato disabilitato dall'admin, il componente non viene renderizzato
 * (coerente con il comportamento della home).
 */

import { useEffect, useState } from "react";
import { api, type HomeBlockDto, type HomeBlockItemDto } from "@/lib/api";
import {
  AudioGalleryBlock,
  ImageGalleryBlock,
  QuoteGalleryBlock,
  VideoGalleryBlock,
} from "./GalleryBlocks";

/** Risolve l'URL di un item: media.url oppure externalUrl (fallback /cms-api/media). */
function resolveItemUrl(item: HomeBlockItemDto): string {
  if (item.media) {
    const url = item.media.url;
    if (url?.startsWith("http")) return url;
    return `/cms-api/media/${item.media.id}`;
  }
  return item.externalUrl ?? "";
}

export function HomeBlockRefRenderer({
  homeBlockId,
  className,
}: {
  homeBlockId?: number;
  className?: string;
}) {
  const [block, setBlock] = useState<HomeBlockDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!homeBlockId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        // 1) Prova la lista pubblica (solo blocchi enabled)
        const publicList = await api.homeBlocks.listPublic();
        if (cancelled) return;
        const found = publicList.find((b) => b.id === homeBlockId);
        if (found) {
          setBlock(found);
          return;
        }

        // 2) Fallback: se il blocco esiste ma è disabilitato (o non ancora
        //    propagato nella cache pubblica), tenta l'endpoint admin.
        //    Se l'utente non è autenticato, fallirà silenziosamente e
        //    mostreremo un messaggio di stato.
        try {
          const adminBlock = await api.homeBlocks.adminGet(homeBlockId);
          if (cancelled) return;
          if (adminBlock) {
            if (!adminBlock.enabled) {
              // Non mostrare blocchi disabilitati agli utenti pubblici.
              // Per un admin logged-in, mostriamo comunque un hint.
              setError(`Home block #${homeBlockId} è disabilitato`);
              setBlock(null);
              return;
            }
            setBlock(adminBlock);
            return;
          }
        } catch {
          // ignore: probabilmente non autenticato → errore generico
        }

        setError(`Home block #${homeBlockId} non trovato o disabilitato`);
        setBlock(null);
      } catch (err) {
        if (cancelled) return;
        console.error("[HomeBlockRefRenderer] errore caricamento:", err);
        setError(err instanceof Error ? err.message : "Errore caricamento home block");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [homeBlockId]);

  if (!homeBlockId) {
    return (
      <div className="max-w-4xl mx-auto my-6 p-4 rounded-lg border border-dashed border-amber-300 bg-amber-50 text-amber-700 text-sm">
        ⚠ Home block non configurato.
      </div>
    );
  }
  if (loading) {
    return (
      <div className="max-w-4xl mx-auto my-6 p-4 rounded-lg border border-neutral-200 bg-neutral-50 text-neutral-500 text-sm text-center">
        Caricamento…
      </div>
    );
  }
  if (error || !block) {
    // Renderizza un placeholder visibile: aiuta l'autore della pagina a capire
    // che il home-block referenziato non è disponibile (disabilitato, cancellato,
    // o errore di rete). Gli utenti pubblici non autenticati vedranno lo stesso
    // hint — è comunque meno dannoso del silenzio totale.
    return (
      <div className="max-w-4xl mx-auto my-6 p-4 rounded-lg border border-dashed border-amber-300 bg-amber-50 text-amber-700 text-xs text-center">
        ⚠ {error ?? "Home block non disponibile"}
        <span className="block text-[10px] text-amber-600/80 mt-1">
          Verifica che il blocco #{homeBlockId} sia abilitato in <code>/admin/home-blocks</code>.
        </span>
      </div>
    );
  }

  return renderHomeBlock(block, className);
}

/** Converte un HomeBlockDto nella gallery specifica (utile anche server-side). */
export function renderHomeBlock(block: HomeBlockDto, className?: string) {
  const cfg = block.configJson ?? {};
  const layout = (cfg.layout as "grid" | "carousel" | "strip" | "list" | "single" | "slideshow" | undefined) ?? undefined;
  const columns = (cfg.columns as 2 | 3 | 4 | undefined) ?? undefined;
  const bgColor = (cfg.bgColor as string | undefined) ?? undefined;
  const slideshowIntervalMs = (cfg.slideshowIntervalMs as number | undefined) ?? undefined;
  const slideshowAutoPlay = (cfg.slideshowAutoPlay as boolean | undefined) ?? undefined;
  const title = block.title ?? undefined;
  const subtitle = block.subtitle ?? undefined;

  switch (block.type) {
    case "IMAGE_GALLERY":
      return (
        <ImageGalleryBlock
          title={title}
          subtitle={subtitle}
          layout={(layout as "grid" | "carousel" | "strip" | "slideshow") ?? "grid"}
          columns={columns}
          bgColor={bgColor}
          slideshowIntervalMs={slideshowIntervalMs}
          slideshowAutoPlay={slideshowAutoPlay}
          className={className}
          items={block.items.map((it) => ({
            url: resolveItemUrl(it),
            caption: it.caption ?? undefined,
          }))}
        />
      );
    case "VIDEO_GALLERY":
      return (
        <VideoGalleryBlock
          title={title}
          subtitle={subtitle}
          layout={(layout as "grid" | "carousel" | "strip" | "slideshow") ?? "grid"}
          columns={columns}
          bgColor={bgColor}
          slideshowIntervalMs={slideshowIntervalMs}
          slideshowAutoPlay={slideshowAutoPlay}
          className={className}
          items={block.items.map((it) => ({
            url: resolveItemUrl(it),
            caption: it.caption ?? undefined,
          }))}
        />
      );
    case "AUDIO_GALLERY":
      return (
        <AudioGalleryBlock
          title={title}
          subtitle={subtitle}
          layout={(layout as "grid" | "list") ?? "list"}
          columns={columns === 4 ? 3 : (columns as 2 | 3 | undefined)}
          bgColor={bgColor}
          className={className}
          items={block.items.map((it) => ({
            url: resolveItemUrl(it),
            caption: it.caption ?? undefined,
          }))}
        />
      );
    case "QUOTE_GALLERY":
      return (
        <QuoteGalleryBlock
          title={title}
          subtitle={subtitle}
          layout={(layout as "grid" | "carousel" | "single" | "slideshow") ?? "grid"}
          columns={columns === 4 ? 3 : (columns as 2 | 3 | undefined)}
          bgColor={bgColor}
          slideshowIntervalMs={slideshowIntervalMs}
          slideshowAutoPlay={slideshowAutoPlay}
          className={className}
          items={block.items.map((it) => ({
            text: it.quoteText ?? "",
            author: it.quoteAuthor ?? undefined,
            source: it.quoteSource ?? undefined,
          }))}
        />
      );
    default:
      return null;
  }
}

